import React from 'react';
import { useParams } from 'react-router';
import CacheResultsContext from '../components/CacheResultsContext';
import ContainerWrapper from '../styled components/ContainerWrapper';
import FeedbackMessage from '../components/FeedbackMessage/FeedbackMessage';
import LoadingState from '../styled components/LoadingState';
import ListViewOverview from '../listview/ListViewOverview';
import ListViewContainer from './ListViewContainer';
import useListViewConfigurations from '../hooks/useListViewConfigurations';
import { cacheResultForViewsFilterOptionsKey } from '../libs/cacheKeys';
import debounce from '../libs/debounce';
import { listViewTitle } from '../libs/listViewSettings';
import type NavigationHistory from '../libs/NavigationHistory';
import type { ListViewConfiguration } from '../listview/types';

/* Container for the Views widget, on both of its pages:
 *
 *   /views          the widget itself. A tenant with a single view gets that
 *                   view right here, as there is nothing to choose between;
 *                   one with several gets an overview to pick one from
 *   /view/:viewID   one view of a tenant with several, opened from that
 *                   overview (an internal widget of its own, the way
 *                   /timesheet is to /timesheets, so the header's back button
 *                   leads back to the overview)
 * */

interface ViewsContainerProps {
  user: { host: string };
  navigationHistory: NavigationHistory;
  linkUrlFor: (url: string) => string;
  setCustomRendererForHeaderTitle: (renderFunction: (() => string) | false) => void;
}

const pathOfView = (view: ListViewConfiguration) =>
  `/view/${encodeURIComponent(view.id)}`;

/* The widget ids of this widget's own two pages (server/widgets.js) */
const widgetIDsOfViews = ['views', 'view'];

const ViewsContainer = ({
  user,
  navigationHistory,
  linkUrlFor,
  setCustomRendererForHeaderTitle
}: ViewsContainerProps) => {
  /* Only present on the page of one view */
  const { viewID } = useParams();
  const { configurations, configurationsFetched, errorCode } = useListViewConfigurations(
    user.host
  );

  /* No id is no match either: every configuration has one */
  const openedView = configurations.find(({ id }) => id === viewID) || null;

  /* Leaving the widget drops the cached filter options, so a later visit sees
     an installation that may have changed since.
     Navigating between the views overview and a view detail page will not clear the cached filter options. */
  const { getCacheResultValue, resetCacheResultValue } =
    React.useContext(CacheResultsContext);
  React.useEffect(
    () => () => {
      const widgetNavigatedTo = navigationHistory.getAppForPath(
        navigationHistory.history.location.pathname
      );
      if (widgetNavigatedTo && widgetIDsOfViews.includes(widgetNavigatedTo.id)) return;
      const cacheKey = cacheResultForViewsFilterOptionsKey(user.host);
      if (getCacheResultValue(cacheKey)) resetCacheResultValue(cacheKey);
    },
    []
  );

  /* The header names the view that is open. Opening it from the overview
     passes its name along with the navigation already; this covers landing on
     the view's page directly, when the header only knows it is on a view */
  const openedViewTitle = openedView ? listViewTitle(openedView) : null;
  React.useEffect(() => {
    if (openedViewTitle === null) return undefined;
    setCustomRendererForHeaderTitle(() => openedViewTitle);
    return () => setCustomRendererForHeaderTitle(false);
  }, [openedViewTitle]);


  const openView = React.useMemo(
    () =>
      debounce((view: ListViewConfiguration) =>
        navigationHistory.goTo({
          pathname: linkUrlFor(pathOfView(view)),
          state: { headerTitle: listViewTitle(view) }
        })
      ),
    [navigationHistory, linkUrlFor]
  );

  const renderView = (configuration: ListViewConfiguration) => (
    <ListViewContainer
      key={configuration.id}
      host={user.host}
      configuration={configuration}
    />
  );

  const content = () => {
    if (errorCode)
      return (
        <FeedbackMessage
          variant="error"
          feedbackTexts={[i18n('The view could not be loaded')]}
          dataAttributes={{ 'data-test-type': 'errorMessage' }}
        />
      );
    /* Nothing of either page can be laid out before the configurations are
       there, so this one loading state does cover the whole widget */
    if (!configurationsFetched)
      return (
        <div tw="relative py-32" role="status" aria-label={i18n('Loading…')}>
          <LoadingState $size="small" />
        </div>
      );
    if (viewID !== undefined) {
      /* A stale link: the view has been removed since, or cannot be rendered
         by this client */
      if (!openedView)
        return (
          <FeedbackMessage
            variant="info"
            feedbackTexts={[i18n('This view does not exist (anymore)')]}
            dataAttributes={{ 'data-test-type': 'viewNotFoundMessage' }}
          />
        );
      return renderView(openedView);
    }
    if (configurations.length === 0)
      return (
        <FeedbackMessage
          variant="info"
          feedbackTexts={[i18n('No views have been configured yet')]}
          dataAttributes={{ 'data-test-type': 'noConfigurationMessage' }}
        />
      );
    if (configurations.length === 1) return renderView(configurations[0]);
    return (
      <ListViewOverview
        views={configurations}
        hrefForView={(view) => linkUrlFor(pathOfView(view))}
        onOpenView={openView}
      />
    );
  };

  return (
    <ContainerWrapper $paddingY={16}>
      <div tw="mt-48">{content()}</div>
    </ContainerWrapper>
  );
};

export default ViewsContainer;
